import type { IOCType } from './iocClassifier';
import { classifyIOC } from './iocClassifier';

// ── Single source of truth for entity colors ────────────────────────────────
// Every entity type gets one recognizable color across explore badges, graph
// nodes, legends, and sidebars.

export const ENTITY_COLORS = {
  hash:      '#F0A850',  // warm amber
  ip:        '#E06050',  // coral red
  domain:    '#50B8A0',  // teal
  url:       '#5898D8',  // sky blue
  email:     '#A878E0',  // lavender
  filepath:  '#88A830',  // olive green
  cve:       '#D068E0',  // magenta
  technique: '#E86888',  // rose pink
  actor:     '#F09848',  // orange
  family:    '#E85858',  // red
  country:   '#68A8C8',  // muted teal-blue
} as const;

export type EntityCategory = keyof typeof ENTITY_COLORS;

// ── Graph node label → color (backward-compatible with old NODE_COLORS) ─────

export const NODE_COLORS: Record<string, string> = {
  MalwareFamily:   ENTITY_COLORS.family,
  ThreatActor:     ENTITY_COLORS.actor,
  IOC:             ENTITY_COLORS.hash,       // fallback for unclassified IOC
  CVE:             ENTITY_COLORS.cve,
  Document:        '#58C878',                // green — graph-only
  AttackTechnique: ENTITY_COLORS.technique,
  AttackTactic:    ENTITY_COLORS.technique,
  AttackGroup:     ENTITY_COLORS.actor,
  AttackSoftware:  ENTITY_COLORS.family,
  WebSource:       '#58A8E8',                // blue — graph-only
  Country:         ENTITY_COLORS.country,
};

// ── IOC sub-type → color (backward-compatible with old IOC_COLORS) ──────────

export const IOC_COLORS: Record<IOCType, string> = {
  HASH:    ENTITY_COLORS.hash,
  IP:      ENTITY_COLORS.ip,
  DOMAIN:  ENTITY_COLORS.domain,
  URL:     ENTITY_COLORS.url,
  EMAIL:   ENTITY_COLORS.email,
  UNKNOWN: '#888888',
};

// ── Group palette (Explore tab strip) ───────────────────────────────────────
// One color per registry group; sub-type chips inherit their group color.

export const GROUP_COLORS: Record<string, string> = {
  hashes:   ENTITY_COLORS.hash,
  network:  ENTITY_COLORS.ip,
  endpoint: ENTITY_COLORS.filepath,
  mitre:    ENTITY_COLORS.technique,
  crypto:   '#E0B040',  // gold
  handles:  '#6890C8',  // muted blue
  misc:     '#808890',  // slate gray
};

// One-off category color overrides — used when a category needs to break
// from its group color (e.g. to disambiguate sibling chips).
const CATEGORY_OVERRIDES: Record<string, string> = {
  asn:   '#C8585F',  // dusty rose — clearly distinct from IP coral
  cidr:  '#E08858',  // burnt orange
  onion: '#7848A0',  // deep purple — Tor branding nod, contrasts the others
};

// Map registry sub-category keys to a top-level color group. Anything not
// listed falls back to its group's color via groupColor().
const CATEGORY_KEY_TO_GROUP: Record<string, string> = {
  // Network
  ip: 'network', domain: 'network', url: 'network', email: 'network',
  asn: 'network', cidr: 'network', onion: 'network',
  // Endpoint
  filepath: 'endpoint', registry: 'endpoint', pdb: 'endpoint', yara: 'endpoint',
  // Hashes
  hash: 'hashes',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Resolve a graph node to its color, using IOC sub-type when label is 'IOC'. */
export function nodeColor(label: string, name?: string): string {
  if (label === 'IOC' && name) {
    return IOC_COLORS[classifyIOC(name)];
  }
  return NODE_COLORS[label] ?? '#aaaaaa';
}

/** Resolve a registry group key (e.g. "network", "mitre") to its color. */
export function groupColor(group: string): string {
  return GROUP_COLORS[group] ?? '#888888';
}

/** Resolve an explore-page category key to its color.
 *
 * Tries — in order — the legacy ENTITY_COLORS map, the registry-key→group map
 * (so e.g. `asn` inherits the network color), and finally the category key's
 * dotted prefix as a group key (so `crypto.btc` resolves via `crypto`).
 */
export function categoryColor(category: string): string {
  if (category in ENTITY_COLORS) return ENTITY_COLORS[category as EntityCategory];
  if (category in CATEGORY_OVERRIDES) return CATEGORY_OVERRIDES[category];
  const grp = CATEGORY_KEY_TO_GROUP[category];
  if (grp) return GROUP_COLORS[grp];
  if (category.includes('.')) {
    const prefix = category.split('.')[0];
    if (prefix in GROUP_COLORS) return GROUP_COLORS[prefix];
  }
  return '#888888';
}
